import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CalendarDays, Check, X, Plus, Loader2, MapPin, Trophy } from 'lucide-react';

/**
 * PublicBookMePage — what the OPPOSING school sees at /bookme/:token.
 *
 * This is the only OL screen a rival school ever looks at, so it is deliberately
 * styled like a public booking page rather than an admin panel: one centred
 * card, generous spacing, the dates as big tappable options, and no OL chrome,
 * navigation or jargon. No account, no login.
 *
 * They can accept a date, suggest different ones, or decline. The host always
 * confirms afterwards, so nothing is scheduled behind anyone's back.
 */
export default function PublicBookMePage() {
  const { token } = useParams();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api/booking-proposals/public/${token}`;

  const [p, setP] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const [picked, setPicked] = useState('');
  const [mode, setMode] = useState('pick');        // pick | counter | decline
  const [counter, setCounter] = useState(['', '']);
  const [note, setNote] = useState('');
  const [who, setWho] = useState('');

  useEffect(() => {
    axios.get(API)
      .then(({ data }) => setP(data.proposal))
      .catch(e => setErr(e.response?.data?.detail || 'This link is not valid.'))
      .finally(() => setLoading(false));
  }, [API]);

  const send = async (action) => {
    setBusy(true);
    try {
      const body = { action, note, responder_name: who };
      if (action === 'accept') body.date_id = picked;
      if (action === 'counter') body.counter_dates = counter.map(d => d.trim()).filter(Boolean);
      const { data } = await axios.post(`${API}/respond`, body);
      setP(data.proposal);
      setDone(action);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Something went wrong — please try again.');
    } finally { setBusy(false); }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso + 'T12:00:00').toLocaleDateString(undefined,
        { weekday: 'long', month: 'long', day: 'numeric' });
    } catch { return iso; }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-6">
          <div className="text-xs uppercase tracking-widest text-slate-400">OmniLeagues</div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-7">{children}</div>
        <p className="text-center text-xs text-slate-400 mt-6">
          No account needed — this link was sent just to you.
        </p>
      </div>
    </div>
  );

  if (loading) return (
    <Shell><div className="flex items-center justify-center gap-2 text-slate-500 py-10">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading…
    </div></Shell>
  );

  if (err && !p) return (
    <Shell>
      <h1 className="text-lg font-semibold text-slate-900">This link isn’t available</h1>
      <p className="text-sm text-slate-500 mt-2">{err}</p>
      <p className="text-sm text-slate-500 mt-3">Ask the other school to send a fresh link.</p>
    </Shell>
  );

  if (done) return (
    <Shell>
      <div className="text-center py-4">
        <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
          <Check className="h-6 w-6 text-emerald-600" />
        </div>
        <h1 className="text-xl font-semibold text-slate-900">Thank you</h1>
        <p className="text-sm text-slate-500 mt-2">
          {done === 'accept' && `${p.from_org_name} has been told which date works.`}
          {done === 'counter' && `Your suggested dates have been sent to ${p.from_org_name}.`}
          {done === 'decline' && `${p.from_org_name} has been told these dates don’t work.`}
        </p>
        <p className="text-sm text-slate-500 mt-2">They’ll confirm the fixture from their side.</p>
      </div>
    </Shell>
  );

  const open = (p.dates || []).filter(d => d.status !== 'rejected');

  return (
    <Shell>
      <div className="flex items-start gap-3 pb-5 border-b border-slate-100">
        <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
          <Trophy className="h-5 w-5 text-amber-600" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-slate-900 leading-tight">
            {p.from_org_name} would like to schedule a game
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {p.sport_label} · {p.team_label} · {p.season}
          </p>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-4 py-5 text-sm">
        <div>
          <dt className="text-slate-400 text-xs uppercase tracking-wide">Playing</dt>
          <dd className="text-slate-900 mt-0.5">
            {p.home_away_for_peer === 'home' ? 'At your venue' : `At ${p.from_org_name}`}
          </dd>
        </div>
        {p.location && (
          <div>
            <dt className="text-slate-400 text-xs uppercase tracking-wide">Venue</dt>
            <dd className="text-slate-900 mt-0.5 flex items-center gap-1">
              <MapPin className="h-3 w-3 text-slate-400" />{p.location}
            </dd>
          </div>
        )}
      </dl>

      {p.note && (
        <p className="text-sm text-slate-600 bg-slate-50 rounded-lg px-4 py-3 mb-5">“{p.note}”</p>
      )}

      {p.status === 'confirmed' ? (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800">
          This fixture is confirmed. Nothing more is needed from you.
        </div>
      ) : (
        <>
          {mode === 'pick' && (
            <>
              <h2 className="text-sm font-semibold text-slate-900 mb-3">
                Which date works for you?
              </h2>
              <div className="space-y-2">
                {open.map(d => (
                  <button key={d.id} onClick={() => setPicked(d.id)}
                    className={`w-full text-left px-4 py-3 rounded-xl border transition
                      ${picked === d.id
                        ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-500'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}
                    data-testid={`peer-date-${d.id}`}>
                    <div className="flex items-center gap-3">
                      <CalendarDays className={`h-4 w-4 ${picked === d.id ? 'text-amber-600' : 'text-slate-400'}`} />
                      <span className="text-slate-900 font-medium">{fmt(d.date)}</span>
                      {d.time && <span className="text-slate-500 text-sm">{d.time}</span>}
                      {d.proposed_by === 'peer' && (
                        <span className="ml-auto text-[11px] text-slate-400">you suggested this</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              <input value={who} onChange={e => setWho(e.target.value)}
                placeholder="Your name (optional)"
                className="mt-5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                data-testid="peer-name" />

              <button onClick={() => send('accept')} disabled={!picked || busy}
                className="mt-3 w-full rounded-xl bg-amber-600 hover:bg-amber-700 disabled:opacity-40
                           text-white font-medium py-3 flex items-center justify-center gap-2"
                data-testid="peer-accept">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Confirm this date
              </button>

              <div className="flex items-center gap-4 mt-4 justify-center text-sm">
                <button onClick={() => setMode('counter')} className="text-slate-500 hover:text-slate-900 underline"
                  data-testid="peer-counter-mode">None of these work</button>
                <span className="text-slate-300">·</span>
                <button onClick={() => setMode('decline')} className="text-slate-500 hover:text-slate-900 underline"
                  data-testid="peer-decline-mode">We can’t play</button>
              </div>
            </>
          )}

          {mode === 'counter' && (
            <>
              <h2 className="text-sm font-semibold text-slate-900 mb-1">Suggest other dates</h2>
              <p className="text-sm text-slate-500 mb-3">
                {p.from_org_name} will see these and confirm one.
              </p>
              {counter.map((c, i) => (
                <input key={i} type="date" value={c}
                  onChange={e => setCounter(v => { const n = [...v]; n[i] = e.target.value; return n; })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mb-2"
                  data-testid={`peer-counter-${i}`} />
              ))}
              <button onClick={() => setCounter(v => [...v, ''])}
                className="text-sm text-slate-500 hover:text-slate-900 underline mb-3 flex items-center gap-1">
                <Plus className="h-3 w-3" />another date
              </button>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                placeholder="Anything they should know? (optional)"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                data-testid="peer-counter-note" />
              <input value={who} onChange={e => setWho(e.target.value)} placeholder="Your name (optional)"
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              <button onClick={() => send('counter')} disabled={busy || !counter.some(c => c.trim())}
                className="mt-3 w-full rounded-xl bg-amber-600 hover:bg-amber-700 disabled:opacity-40
                           text-white font-medium py-3 flex items-center justify-center gap-2"
                data-testid="peer-counter-send">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Send these dates
              </button>
              <button onClick={() => setMode('pick')}
                className="mt-3 w-full text-sm text-slate-500 hover:text-slate-900">Back</button>
            </>
          )}

          {mode === 'decline' && (
            <>
              <h2 className="text-sm font-semibold text-slate-900 mb-1">Let them know you can’t play</h2>
              <p className="text-sm text-slate-500 mb-3">
                This closes the request. They can always send a new one.
              </p>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
                placeholder="Reason (optional)"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                data-testid="peer-decline-note" />
              <input value={who} onChange={e => setWho(e.target.value)} placeholder="Your name (optional)"
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              <button onClick={() => send('decline')} disabled={busy}
                className="mt-3 w-full rounded-xl bg-slate-800 hover:bg-slate-900 text-white
                           font-medium py-3 flex items-center justify-center gap-2"
                data-testid="peer-decline-send">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                Send
              </button>
              <button onClick={() => setMode('pick')}
                className="mt-3 w-full text-sm text-slate-500 hover:text-slate-900">Back</button>
            </>
          )}
        </>
      )}

      {err && <p className="text-sm text-red-600 mt-4">{err}</p>}
    </Shell>
  );
}
